import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { fmtDateTime } from '../lib/format';
import { useAuth } from '../stores/auth.store';
import { Pagination } from '../components/Pagination';

const PAGE_SIZE = 15;

interface PendingOfficer {
  id: string;
  firstName: string;
  lastName: string;
  opdCardNo: string | null;
  phone: string | null;
  linePictureUrl: string | null;
  createdAt: string;
  unit?: { id: string; name: string } | null;
}

export function SupervisorOfficersPage() {
  const officer = useAuth((s) => s.officer);
  const navigate = useNavigate();
  const [items, setItems] = useState<PendingOfficer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get<{ data: PendingOfficer[]; total: number }>(
        '/app/sv/officers/pending',
        { params: { page, pageSize: PAGE_SIZE } },
      );
      setItems(data.data);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [page]);

  if (officer && officer.role === 'officer') {
    return <Navigate to="/" replace />;
  }

  async function approve(id: string, name: string) {
    if (!confirm(`อนุมัติ "${name}" ให้เข้าใช้ระบบ?`)) return;
    setBusy(id);
    try {
      await api.patch(`/app/sv/officers/${id}/approve`);
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'อนุมัติไม่สำเร็จ');
    } finally {
      setBusy(null);
    }
  }

  async function reject(id: string, name: string) {
    const reason = prompt(`เหตุผลปฏิเสธ "${name}" (ไม่บังคับ):`);
    if (reason === null) return;
    setBusy(id);
    try {
      await api.patch(`/app/sv/officers/${id}/reject`, reason ? { reason } : {});
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'ปฏิเสธไม่สำเร็จ');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto min-h-screen max-w-md bg-gray-50 pb-24">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gray-100 bg-white px-4 py-3">
        <button onClick={() => navigate(-1)} className="text-2xl text-gray-500">
          ‹
        </button>
        <div>
          <h1 className="text-base font-semibold">อนุมัติเจ้าหน้าที่</h1>
          <p className="text-xs text-gray-500">รออนุมัติ {total} คน</p>
        </div>
      </header>

      <main className="space-y-3 p-4">
        {loading ? (
          <div className="card p-6 text-center text-sm text-gray-500">กำลังโหลด...</div>
        ) : items.length === 0 ? (
          <div className="card p-8 text-center">
            <div className="text-4xl">✨</div>
            <p className="mt-3 text-sm font-medium">ไม่มีรายการรออนุมัติ</p>
            <p className="mt-1 text-xs text-gray-500">เจ้าหน้าที่ทุกคนผ่านการอนุมัติแล้ว</p>
          </div>
        ) : (
          items.map((o) => (
            <div key={o.id} className="card space-y-3">
              <div className="flex items-start gap-3">
                {o.linePictureUrl ? (
                  <img
                    src={o.linePictureUrl}
                    alt=""
                    className="h-12 w-12 shrink-0 rounded-full object-cover ring-1 ring-gray-200"
                  />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                    👤
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {o.firstName} {o.lastName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {o.opdCardNo ? `เลขบัตร ${o.opdCardNo}` : 'ไม่ระบุเลขบัตร'}
                    {o.unit && ` • ${o.unit.name}`}
                  </p>
                  {o.phone && <p className="text-xs text-gray-500">โทร {o.phone}</p>}
                  <p className="mt-1 text-[11px] text-gray-400">
                    สมัคร {fmtDateTime(o.createdAt)}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => approve(o.id, `${o.firstName} ${o.lastName}`)}
                  disabled={busy === o.id}
                  className="flex-1 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white active:bg-emerald-700 disabled:opacity-50"
                >
                  {busy === o.id ? '...' : '✓ อนุมัติ'}
                </button>
                <button
                  type="button"
                  onClick={() => reject(o.id, `${o.firstName} ${o.lastName}`)}
                  disabled={busy === o.id}
                  className="flex-1 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-red-600 active:bg-red-50 disabled:opacity-50"
                >
                  ✕ ปฏิเสธ
                </button>
              </div>
            </div>
          ))
        )}

        <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
      </main>
    </div>
  );
}
